// The connection "kit": the shared vocabulary every per-entity connection module
// (the sibling `Connections/<Model>.ts` files) is built on. A `Connection` binds
// one generated schema entity to its typed transport — the REST fetcher (#661),
// the live subscription token(s) (#662), its owner-FK index fields (derived from
// the schema graph #666/#1090), soft-delete field and default sort. This is the
// hand-written map criterion 3 asks for: assembled into a `Record<SchemaEntity,
// Connection>` (see ../Connections.ts) that is exhaustive at the type level (a
// missing entity is a compile error) and validated at runtime by `bindSchema`.
//
// #1707 (tracker #1677) extracted the generic schema/store/connection kernel
// below into app-base's `data/schema` + `data/connection` (a `DataSchema`-
// parameterized engine with no knowledge of Logbook's generated types). This
// module is now the adapter: `LogbookSchema` binds the generic engine to
// Logbook's generated `SchemaEntityTypes`/`SchemaEntityFkFields`, `logbookEdges`
// translates the generated schema graph's `kind`/`canonical` fields into the
// generic engine's single `canonical` flag, and every type/helper below keeps
// its exact prior name and shape so every `Connections/<Model>.ts` module and
// View-level consumer (`EntityFilter<K>`, `entity(...)`, …) is unaffected.
//
// `Connection.fetch` keeps Logbook's concrete generated `ApiClient` (rather
// than the generic engine's own `DataApiClient`) as its client parameter, so a
// per-entity fetcher can keep constructing a generated API class directly
// (`new LogApi(client)`) with no per-file adapter. `entity()` is the one seam
// that bridges the two — see its own comment below.
//
// The `entity()` helper auto-derives `indexes` and `softDelete` from the schema
// graph (via app-base's `buildConnection`) and `live` from the naming-convention
// `Subscription` lookup (Logbook policy, not something the generic engine
// assumes); a per-entity module only spells out what is genuinely bespoke (a
// fetcher, a sort, folds, …). A bare `entity("X")` is therefore a schema-derived
// placeholder (indexes + soft-delete + live token where one exists, no fetcher);
// #1105 (data-layer core, tracker #665) wires `Log` (plus Track/Status) end-to-end and later work fleshes out the
// rest.
import {
  buildConnection,
  entityEntries as genericEntityEntries,
  entityIdEntries as genericEntityIdEntries,
  ownerFkIndexes as genericOwnerFkIndexes,
  validateSchema as genericValidateSchema,
  type Connection as GenericConnection,
  type ConnectionRegistry as GenericConnectionRegistry,
  type EntityIds as GenericEntityIds,
  type EntityRows as GenericEntityRows,
  type Fold as GenericFold,
  type FetchResult as GenericFetchResult,
  type LiveToken,
  type SchemaDescriptor,
} from "app-base/data/connection"
import type {
  BoundIds,
  DataSchema,
  EntityFilter as GenericEntityFilter,
  EntityOf as GenericEntityOf,
  FetchIds as GenericFetchIds,
  FilterFields as GenericFilterFields,
  Identified,
  OwnIdField as GenericOwnIdField,
  SchemaEdge as GenericSchemaEdge,
} from "app-base/data/schema"
import type { ApiClient } from "../../Types/Generated/LogbookApi/ApiRuntime"
import { Subscription, type SubscriptionToken } from "../../Types/Generated/LogbookApi/Subscriptions"
import { schemaEdges, type SchemaEdge, type SchemaEntity, type SchemaEntityFkFields, type SchemaEntityTypes } from "../../Types/Generated/LogbookModel/Schema"
import type { IAccountUser } from "../../Types/Generated/LogbookModel/AccountUser"
import type { IStoredLogCalculation } from "../../Types/Generated/LogbookModel/Log"

// `Identified`: every persisted model is identified by a numeric primary key.
// `BoundIds`: owner ids bound at query time (a subset of an entity's FK
// fields), e.g. a Log query binds `{ shipId, dayId }`. Values may be absent
// until resolved.
export type { Identified, BoundIds }

// The concrete model interface for a schema entity (e.g. EntityOf<"Log"> = ILog).
export type EntityOf<K extends SchemaEntity> = GenericEntityOf<LogbookSchema, K>;

export type StoredAccountUser =
  & Identified
  & Pick<IAccountUser, "accountId" | "userId" | "accountLevel">
  & Partial<Omit<IAccountUser, "accountId" | "userId" | "accountLevel">>;

// The shape an entity is STORED in, which is its generated model for every entity except
// `StoredCalculation`: its raw row/broadcast (an `IStoredCalculation` carrying a `data`
// JSON string) is normalized to the folded `IStoredLogCalculation` shape before upsert,
// so that — not the generated model — is what the slice actually holds. See
// `Connection.normalizeLive` and `Connections/StoredCalculation.ts`. `AccountUser`'s wire
// shape (`IAccountUser`) similarly diverges: `userId` is its store identity inside an
// account-scoped store (see `Connections/AccountUser.ts#normalizeAccountUser`).
export type StoredOf<K extends SchemaEntity> =
  K extends "StoredCalculation" ? IStoredLogCalculation
    : K extends "AccountUser" ? StoredAccountUser
      : EntityOf<K>;

// The generated-schema descriptor the generic `app-base/data/*` engine is
// parameterized by (see `DataSchema` in `app-base/data/schema`): Logbook's
// generated wire types, the shape each entity is actually stored in when that
// diverges (`AccountUser`/`StoredCalculation` above), and the FK field union
// each entity may be filtered/queried by.
//
// `types`/`fkFields` are re-keyed as homomorphic mapped types over the
// generated `SchemaEntityTypes`/`SchemaEntityFkFields` interfaces rather than
// referencing them directly: a named `interface` (which supports declaration
// merging) is not provably assignable to `DataSchema`'s `Record<string, ...>`
// bounds, while a mapped type — like `stored` below — is a "fresh", sealed
// object type `tsc` accepts. Structurally identical to the generated
// interfaces; this is a type-level adapter step only, not a behavior change.
export interface LogbookSchema extends DataSchema {
  readonly types: { [K in SchemaEntity]: SchemaEntityTypes[K] };
  readonly stored: { [K in SchemaEntity]: StoredOf<K> };
  readonly fkFields: { [K in SchemaEntity]: SchemaEntityFkFields[K] };
}

// The generated schema graph, translated into the generic engine's single
// `canonical` edge flag. Logbook's generated `SchemaEdge` additionally carries a
// `kind` ("owner" | "index" | "role" | "polymorphic"); the previous hand-written
// `Deps.ts#edgeFk` treated `kind === "owner"` as at least as strong a signal as
// `canonical` (a real edge — `Account -> User` below — can be `kind: "owner"`
// while `canonical: false`). Folding that into one boolean here keeps
// `edgeFk`/`ownerFkIndexes` (both re-derived from app-base's generic versions
// against this array) behaviorally identical to the pre-extraction versions.
export const logbookEdges: readonly GenericSchemaEdge<SchemaEntity>[] = schemaEdges.map((edge: SchemaEdge) => ({
  from: edge.from,
  to: edge.to,
  fkField: edge.fkField,
  canonical: edge.kind === "owner" || edge.canonical,
}));

// The 7 entities carrying a soft-delete `deletedAt` column (verified against the
// generated models). A live broadcast of one of these with `deletedAt` set is a
// removal, not an upsert. `bindSchema` pins this against the connections.
export const softDeleteEntities: ReadonlySet<SchemaEntity> = new Set<SchemaEntity>([
  "Account",
  "AccountUser",
  "DataRecord",
  "Note",
  "NoteAttachment",
  "ShipUser",
  "Status",
  "User",
]);

const logbookSoftDeleteFields: Partial<Record<SchemaEntity, string>> = Object.fromEntries(
  Array.from(softDeleteEntities, (type) => [type, "deletedAt"]),
);

// The full descriptor `bindSchema` validates the hand-written registry against,
// and `entity()`/`buildConnection` derive `indexes`/`softDelete` from.
export const logbookSchemaDescriptor: SchemaDescriptor<SchemaEntity> = {
  edges: logbookEdges,
  softDeleteFields: logbookSoftDeleteFields,
};

// The fields an entity may be filtered on: the FK columns living on it, taken straight
// from the generated schema graph (`SchemaEntityFkFields`, emitted by TypeGen). This makes
// a typo (`dayIdd`), another entity's field (`shipUserId` on a `Log`), a numeric non-FK
// (`timeUtc`) or the primary key (`id`) a compile error rather than a query that silently
// returns nothing — `candidateIds` finds no index bucket for a non-FK field and so matches
// no rows. An entity nothing points at maps to `never`, so filtering it is rejected too.
export type FilterFields<K extends SchemaEntity> = GenericFilterFields<LogbookSchema, K>;

// `BoundIds` narrowed to one entity's own FK fields — the typed form every query surface
// takes. Values stay optional so a caller may bind a subset (`{ dayId }` of Log's six).
export type EntityFilter<K extends SchemaEntity> = GenericEntityFilter<LogbookSchema, K>;

// An entity's own identity field as an owner id spells the entity name + `Id` (a `Ship` is
// bound by `shipId`), which is how a route parameterized by the entity itself names it.
export type OwnIdField<K extends SchemaEntity> = GenericOwnIdField<K>;

// The ids a fetch may be bound by: the entity's FK fields PLUS its own id. A fetch maps to
// a REST route, which is routinely parameterized by the entity itself (`ships.get` takes
// `shipId`) — unlike a store query, which matches FK columns on the stored rows and has no
// index for the entity's own id (that is the `id` primary key).
export type FetchIds<K extends SchemaEntity> = GenericFetchIds<LogbookSchema, K>;

// A side-normalizing bundle of entity rows, keyed by entity type. Each key's rows are
// that entity's own stored type, so filing a `Status` row under `Log` — which
// `applyBundle` would happily upsert into the Log slice, corrupting it with no runtime
// complaint — is a compile error.
export type EntityRows = GenericEntityRows<LogbookSchema>;

// The same shape for id-only payloads (removals, provisional temp ids).
export type EntityIds = GenericEntityIds<LogbookSchema>;

// Typed iteration over an entity-keyed bundle. `Object.entries` widens the key to `string`
// and makes every value possibly-undefined, forcing a cast at each use; these keep the
// `SchemaEntity` key and skip absent slices. The per-entity row correlation is enforced
// where the bundle is *built* (see `EntityRows`) — a heterogeneous loop can only see the
// common `Identified` shape.
export function entityEntries(rows?: EntityRows): [SchemaEntity, readonly Identified[]][] {
  return genericEntityEntries<LogbookSchema>(rows);
}

export function entityIdEntries(ids?: EntityIds): [SchemaEntity, readonly number[]][] {
  return genericEntityIdEntries<LogbookSchema>(ids);
}

// The result of a connection's fetch: a side-normalizing bundle. One request can
// carry several entity types at once (e.g. `logs.list` returns logs AND statuses
// AND calculations) plus non-entity `extras` (view-local data that isn't a
// persisted schema entity, e.g. `auditLogEdits`). The store upserts every entity
// slice and stashes the extras in a single pass.
export type FetchResult = GenericFetchResult<LogbookSchema>;

// An opt-in virtual relationship resolved client-side against the store: a fold
// stitches data from another entity onto each row of the base query, but ONLY when
// a deps declaration names it (see Relationship.with). `source`/`via` locate the
// related rows (e.g. StoredCalculation indexed by `sourceId`), `where` narrows them
// (e.g. to log-sourced calcs), and `apply` projects the result onto the base row.
// `apply` may attach fields the base entity's type doesn't declare (e.g.
// `calculatedV2`), so it is intentionally typed loosely; it MUST return the base
// row unchanged (same reference) when it has nothing to fold, to preserve snapshot
// stability.
export type Fold = GenericFold<LogbookSchema>;

export interface Connection<K extends SchemaEntity = SchemaEntity> {
  // The schema entity this connection is for; always equal to its registry key.
  readonly type: K;
  // FK fields to index this entity by, derived from the incoming schema edges.
  readonly indexes: readonly string[];
  // Field whose presence on a payload marks a soft delete (remove from store).
  readonly softDelete?: string;
  // Derived fields carried by fetched/seeded rows but omitted from raw live
  // broadcasts. Preserve them when replacing the stored base row.
  readonly preserveLive?: readonly string[];
  // Ordering applied to query results.
  readonly sort?: (a: EntityOf<K>, b: EntityOf<K>) => number;
  // Live subscription token(s) routed into the store for this entity type.
  readonly live: readonly LiveToken<EntityOf<K>>[];
  // Typed fetcher resolving a query's bound owner ids to a side-normalizing bundle.
  // Ids are this entity's FK fields plus its own id (see `FetchIds`), so a fetcher
  // destructuring a field the entity can't be bound by is a compile error. Values stay
  // optional, which is why each fetcher guards the ids it actually requires. Takes
  // Logbook's concrete generated `ApiClient` (not the generic engine's `DataApiClient`)
  // so a fetcher can construct a generated API class directly — see this file's header.
  readonly fetch?: (client: ApiClient, ids: FetchIds<K>) => Promise<FetchResult>;
  // This entity's rows arrive as a side effect of another entity's fetch (e.g.
  // Status rides the Log bundle — there is no per-day statuses endpoint). A fetch
  // for this type delegates to the provider's fetch, so sibling deps collapse to
  // one request.
  readonly providedBy?: SchemaEntity;
  // Opt-in virtual relationships this entity exposes, keyed by fold name. A deps
  // declaration requests them via `many(type, { with: [...] })`.
  readonly folds?: Readonly<Record<string, Fold>>;
  // Maps a raw live broadcast into this entity's stored shape before upsert
  // (identity for every entity except StoredCalculation, whose broadcast is the
  // raw `IStoredCalculation` — a `data` JSON string — normalized to the folded
  // `IStoredLogCalculation` shape). Applied in `applyLive` before the delete/upsert
  // decision; must preserve `id` so deletes still route.
  readonly normalizeLive?: (raw: Identified) => Identified;
  // Maps a wire entity without the store's numeric identity into its normalized
  // stored shape. AccountUser uses userId as its identity inside an
  // account-scoped store; ordinary entities omit this and already carry `id`.
  readonly normalize?: (raw: EntityOf<K>) => StoredOf<K>;
}

// The full, exhaustive registry: a mapped type over the whole `SchemaEntity`
// union, so omitting an entity fails `tsc`.
export type ConnectionRegistry = { [K in SchemaEntity]: Connection<K> };

// FK fields to index an entity by: every schema edge that targets it, deduped.
// Log → { accountId, cruiseId, dayId, shipId, tripId, userId }.
export function ownerFkIndexes(type: SchemaEntity): string[] {
  return genericOwnerFkIndexes(type, logbookEdges);
}

// Widen a Logbook-typed connection registry (whose `Connection.fetch` fields
// are typed against the concrete generated `ApiClient`) to the generic
// engine's own registry shape (typed against its wider `DataApiClient`) — the
// constructor-time counterpart of `entity()`'s own widening (see its comment):
// a genuine `ApiClient` value always satisfies `DataApiClient` structurally,
// and neither `validateSchema` nor the generic `DataStore` (../Store.ts) ever
// constructs a `DataApiClient` of their own to hand to a connection's `fetch` —
// they only ever forward the same concrete `ApiClient` a caller supplied.
export function toGenericConnections(registry: ConnectionRegistry): GenericConnectionRegistry<LogbookSchema> {
  return registry as GenericConnectionRegistry<LogbookSchema>;
}

// Runtime exhaustiveness guard for the connection layer (see ../BindSchema.ts),
// delegating to app-base's generic `validateSchema` against
// `logbookSchemaDescriptor`.
export function validateSchema(registry: ConnectionRegistry): void {
  genericValidateSchema<LogbookSchema>(toGenericConnections(registry), logbookSchemaDescriptor);
}

// The primary live token for an entity, when one exists. `Subscription` is keyed
// by name; the `token.type === type` guard keeps us to the entity's own primary
// token (skipping secondary tokens like `currentLog`) and yields `[]` for the
// four entities with no broadcast (AccountSubscriptionPlan* / AuditLogEdit).
function defaultLive<K extends SchemaEntity>(type: K): SubscriptionToken<EntityOf<K>>[] {
  const token = (Subscription as Record<string, SubscriptionToken<unknown>>)[type];
  return token && token.type === type ? [token as SubscriptionToken<EntityOf<K>>] : [];
}

type EntityOverrides<K extends SchemaEntity> = Partial<
  Pick<Connection<K>, "sort" | "fetch" | "live" | "softDelete" | "preserveLive" | "providedBy" | "folds" | "normalizeLive" | "normalize">
>;

// Build a Connection, auto-deriving `indexes` and `softDelete` from the schema
// graph (via app-base's `buildConnection`) and `live` from the naming-convention
// `Subscription` lookup; callers override `sort`/`fetch`/`live`/folds/etc.
export function entity<K extends SchemaEntity>(type: K, overrides: EntityOverrides<K> = {}): Connection<K> {
  // `buildConnection`'s generic `fetch` takes the engine's own `DataApiClient`
  // (a structural target: string) while every per-entity fetcher is written
  // against Logbook's generated `ApiClient` (target: "app" | "server") so it
  // can construct generated API classes directly (`new LogApi(client)`, …). A
  // concrete `ApiClient` value always satisfies `DataApiClient` structurally
  // (the same relationship `DataApiClient`'s own doc comment describes for
  // `createGeneratedApiClient`) — this is the one adapter seam that asserts
  // it, both widening `overrides` on the way in and narrowing the built
  // connection back on the way out, so every per-entity connection module
  // keeps its unchanged `ApiClient`-typed fetcher.
  type GenericOverrides = Partial<
    Pick<GenericConnection<LogbookSchema, K>, "sort" | "fetch" | "live" | "softDelete" | "preserveLive" | "providedBy" | "folds" | "normalizeLive" | "normalize">
  >;
  return buildConnection<LogbookSchema, K>(type, logbookSchemaDescriptor, {
    ...(overrides as GenericOverrides),
    live: overrides.live ?? defaultLive(type),
  }) as Connection<K>;
}
